import { useState, useEffect } from "react";
import { apiFetch, useAuth } from "../App";
import { bookmakerLabel } from '../utils/bookmakers';

const SPORT_EMOJI = { soccer: "⚽", basketball: "🏀", tennis: "🎾", americanfootball: "🏈", cricket: "🏏", rugby: "🏉" };
function sportEmoji(sport = "") {
  const k = Object.keys(SPORT_EMOJI).find(k => sport.toLowerCase().includes(k));
  return SPORT_EMOJI[k] || "🎯";
}

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return "morning";
  if (h < 17) return "afternoon";
  return "evening";
}

function GradePill({ grade }) {
  const map = { great: ["pill-green", "🔥 GREAT"], good: ["pill-teal", "✅ GOOD"], fair: ["pill-amber", "⚡ FAIR"], weak: ["pill-blue", "WEAK"] };
  const [cls, label] = map[grade] || ["pill-blue", grade];
  return <span className={`pill ${cls}`} style={{ fontSize: 10 }}>{label}</span>;
}

function MiniStat({ label, value, color }) {
  return (
    <div style={{ background: "var(--bg3)", borderRadius: "var(--radius-sm)", padding: "10px 12px" }}>
      <div style={{ fontSize: 10, color: "var(--text3)", marginBottom: 3 }}>{label}</div>
      <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, color: color || "var(--text)", fontSize: 15 }}>{value}</div>
    </div>
  );
}

function TableSkeleton({ rows = 3 }) {
  return (
    <div style={{ padding: "8px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="skeleton" style={{ height: 40 }} />
      ))}
    </div>
  );
}

function DailyParlayCard({ onNavigate }) {
  const { user } = useAuth();
  const [parlays, setParlays] = useState({ basic: null, pro: null });
  const [loading, setLoading] = useState(true);
  const [showMode, setShowMode] = useState('basic');

  useEffect(() => {
    apiFetch('/daily-parlay/today')
      .then(data => setParlays(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const parlay = showMode === 'pro' ? parlays.pro : parlays.basic;
  const STATUS_COLOR = { pending: 'var(--text3)', won: 'var(--success)', lost: 'var(--danger)' };
  const STATUS_ICON = { pending: '⏳', won: '✅', lost: '❌' };

  return (
    <div className="card">
      <div className="section-header">
        <div>
          <div className="section-title">🃏 Today's AI Parlay</div>
          <div className="section-sub">Built at 8:00 AM · Updated daily</div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {/* Mode toggle */}
          <div style={{ display: 'flex', background: 'var(--bg3)', borderRadius: 'var(--radius-sm)', padding: 2 }}>
            <button
              onClick={() => setShowMode('basic')}
              style={{
                padding: '4px 10px', fontSize: 10, fontWeight: 600,
                borderRadius: 'var(--radius-sm)', border: 'none', cursor: 'pointer',
                background: showMode === 'basic' ? 'var(--accent)' : 'transparent',
                color: showMode === 'basic' ? '#000' : 'var(--text3)',
              }}
            >
              Basic
            </button>
            <button
              onClick={() => setShowMode('pro')}
              style={{
                padding: '4px 10px', fontSize: 10, fontWeight: 600,
                borderRadius: 'var(--radius-sm)', border: 'none', cursor: 'pointer',
                background: showMode === 'pro' ? 'var(--accent)' : 'transparent',
                color: showMode === 'pro' ? '#000' : 'var(--text3)',
              }}
            >
              ⭐ Pro
            </button>
          </div>
          <button className="btn btn-outline" style={{ fontSize: 11, padding: '5px 10px' }} onClick={() => onNavigate('smartParlay')}>
            Build →
          </button>
        </div>
      </div>

      {loading ? (
        <TableSkeleton rows={3} />
      ) : !parlay ? (
        <div style={{ padding: '20px 16px', textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
          <div style={{ fontSize: 24, marginBottom: 8 }}>🃏</div>
          <div>Today's parlay hasn't been built yet.</div>
          <div style={{ fontSize: 12, marginTop: 4 }}>Check back after 8:00 AM</div>
        </div>
      ) : (
        <div>
          {/* Status bar */}
          <div style={{
            padding: '8px 16px',
            background: parlay.status === 'won' ? 'rgba(18,201,142,0.06)' : parlay.status === 'lost' ? 'rgba(240,71,71,0.06)' : 'rgba(61,142,248,0.06)',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <div style={{ display: 'flex', gap: 12, fontSize: 12 }}>
              <span style={{ color: 'var(--text3)' }}>{parlay.legs?.length} legs</span>
              <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700 }}>{parlay.combinedOdds}x</span>
              <span style={{ color: 'var(--success)', fontWeight: 600 }}>P{parlay.potentialReturn} potential</span>
            </div>
            <div style={{ fontSize: 12, color: STATUS_COLOR[parlay.status], fontWeight: 600 }}>
              {STATUS_ICON[parlay.status]} {parlay.status.toUpperCase()}
            </div>
          </div>

          {/* Legs */}
          {parlay.legs?.map((leg, i) => (
            <div key={i} style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  ⚽ {leg.match}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                  {leg.market} — {leg.selection}
                  {showMode === 'pro' && leg.confidence && (
                    <span style={{ marginLeft: 6, color: leg.confidence === 'HIGH' ? 'var(--success)' : leg.confidence === 'MEDIUM' ? 'var(--warning)' : 'var(--danger)' }}>
                      · {leg.confidence === 'HIGH' ? '🟢' : leg.confidence === 'MEDIUM' ? '🟡' : '🔴'} {leg.confidence}
                    </span>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--accent)', fontSize: 14 }}>
                  {leg.odds}
                </span>
                {leg.result && leg.result !== 'pending' && (
                  <span>{leg.result === 'won' ? '✅' : '❌'}</span>
                )}
              </div>
            </div>
          ))}

          {/* Win probability */}
          <div style={{ padding: '10px 16px', display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text3)' }}>
            <span>Win probability: {parlay.combinedWinProbability}%</span>
            <span>Stake: P{parlay.stake}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Dashboard({ onNavigate }) {
  const { user } = useAuth();
  const [valueBets, setValueBets] = useState([]);
  const [arbs, setArbs] = useState([]);
  const [bankroll, setBankroll] = useState(null);
  const [clvStats, setClvStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [width, setWidth] = useState(window.innerWidth);

  // Properly responsive — updates on resize/rotation
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  useEffect(() => {
    Promise.all([
      apiFetch("/value-bets?limit=5"),
      apiFetch("/arbitrage?limit=3"),
      apiFetch("/bankroll"),
      apiFetch("/clv?days=30"),
    ]).then(([vb, arb, br, clv]) => {
      setValueBets(vb.bets || []);
      setArbs(arb.arbs || []);
      setBankroll(br);
      setClvStats(clv);
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const isMobile = width < 768;

  const stats = [
    {
      label: "Value Bets",
      value: loading ? "—" : valueBets.length,
      change: "Active right now",
      dir: "up",
      color: "var(--accent)",
      nav: "valuebets",
    },
    {
      label: "Arbitrage",
      value: loading ? "—" : arbs.length,
      change: arbs[0] ? `Best: +${arbs[0].profitPct}%` : "Scanning...",
      dir: "up",
      color: "var(--accent2)",
      nav: "arbitrage",
    },
    {
      label: "Monthly ROI",
      value: bankroll ? `${bankroll.monthlyPL >= 0 ? "+" : ""}${((bankroll.monthlyPL / (bankroll.startingAmount || 1)) * 100).toFixed(1)}%` : "+0.0%",
      change: bankroll ? `P${bankroll.monthlyPL?.toFixed(2)} this month` : "P0.00 this month",
      dir: bankroll?.monthlyPL >= 0 ? "up" : "down",
      color: "var(--accent3)",
      nav: "bankroll",
    },
    {
      label: "Avg CLV",
      value: clvStats ? `${clvStats.avgClv >= 0 ? "+" : ""}${clvStats.avgClv}%` : "+0%",
      change: clvStats ? `${clvStats.positiveClvPct}% bets beat close` : "0% bets beat close",
      dir: clvStats?.avgClv >= 0 ? "up" : "down",
      color: "var(--success)",
      nav: "clv",
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

      {/* Welcome */}
      <div>
        <h1 style={{ fontFamily: "var(--font-display)", fontSize: isMobile ? 22 : 26, fontWeight: 800 }}>
          Good {getGreeting()},{" "}
          <span style={{ color: "var(--accent)" }}>
            {user?.name?.split(" ")[0] || "Bettor"}
          </span> 👋
        </h1>
        <p style={{ color: "var(--text3)", fontSize: 13, marginTop: 4 }}>
          Here's your betting intelligence overview for today
        </p>
      </div>

      {/* Stats row — clickable to navigate */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 12 }}>
        {stats.map(s => (
          <div
            key={s.label}
            className="stat-card"
            style={{ "--accent-color": s.color, cursor: "pointer" }}
            onClick={() => onNavigate(s.nav)}
          >
            <div className="stat-label">{s.label}</div>
            <div className="stat-value">{s.value}</div>
            <div className={`stat-change ${s.dir}`}>
              {s.dir === "up" ? "↑" : "↓"} {s.change}
            </div>
          </div>
        ))}
      </div>

       {/* Daily Parlay Card */}
      <DailyParlayCard onNavigate={onNavigate} />

      {/* Main content grid */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 320px", gap: 16 }}>

        {/* Value bets */}
        <div className="card">
          <div className="section-header">
            <div>
              <div className="section-title">🔥 Top Value Bets</div>
              <div className="section-sub">Sorted by edge — updated every 2 min</div>
            </div>
            <button className="btn btn-outline" style={{ fontSize: 11, padding: "5px 12px" }} onClick={() => onNavigate("valuebets")}>
              View all →
            </button>
          </div>

          {loading ? <TableSkeleton rows={5} /> : valueBets.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🔍</div>
              <p>No value bets found right now. Odds are being refreshed.</p>
            </div>
          ) : isMobile ? (
            <div>
              {valueBets.map(b => (
                <div key={b._id} style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.homeTeam} vs {b.awayTeam}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                      {b.selection} · {bookmakerLabel(b.bookmakeName)}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 12 }}>
                    <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--success)', fontSize: 15 }}>+{b.edgePct}%</div>
                    <GradePill grade={b.grade} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Match</th>
                  <th>Market</th>
                  <th>Book</th>
                  <th style={{ textAlign: "right" }}>Odds</th>
                  <th style={{ textAlign: "right" }}>Edge</th>
                  <th style={{ textAlign: "right" }}>Grade</th>
                </tr>
              </thead>
              <tbody>
                {valueBets.map(b => (
                  <tr key={b._id}>
                    <td><div className="sport-badge" style={{ background: "rgba(0,229,176,0.08)" }}>{sportEmoji(b.sport)}</div></td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{b.homeTeam} vs {b.awayTeam}</div>
                      <div style={{ fontSize: 11, color: "var(--text3)" }}>{b.selection}</div>
                    </td>
                    <td><span style={{ fontSize: 11, color: "var(--text3)" }}>{b.market}</span></td>
                    <td><span className="pill pill-blue" style={{ fontSize: 10 }}>{bookmakerLabel(b.bookmakeName)}</span></td>
                    <td style={{ textAlign: "right", fontFamily: "var(--font-display)", fontWeight: 700 }}>{b.odds}</td>
                    <td style={{ textAlign: "right" }}>
                      <span style={{ fontFamily: "var(--font-display)", fontWeight: 700, color: "var(--success)", fontSize: 13 }}>
                        +{b.edgePct}%
                      </span>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <GradePill grade={b.grade} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Right column */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

          {/* Live arbs */}
          <div className="card">
            <div className="section-header">
              <div>
                <div className="section-title">⚡ Live Arbs</div>
                <div className="section-sub">Guaranteed profit</div>
              </div>
              <button className="btn btn-outline" style={{ fontSize: 11, padding: "5px 10px" }} onClick={() => onNavigate("arbitrage")}>
                All →
              </button>
            </div>
            {loading ? <TableSkeleton rows={3} /> : arbs.length === 0 ? (
              <div style={{ padding: "24px 20px", textAlign: "center", color: "var(--text3)", fontSize: 13 }}>
                Scanning for arbs...
              </div>
            ) : arbs.map(a => (
              <div key={a._id} style={{
                padding: "12px 16px", borderBottom: "1px solid var(--border)",
                display: "flex", alignItems: "center", justifyContent: "space-between",
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 500, color: "var(--text)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {a.homeTeam} vs {a.awayTeam}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 2 }}>{a.sport || 'Football'}</div>
                  <div className="pill pill-red" style={{ marginTop: 4, fontSize: 10 }}>⏱ Act fast</div>
                </div>
                <div style={{ textAlign: "right", flexShrink: 0, marginLeft: 12 }}>
                  <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, color: "var(--success)", fontSize: 18 }}>
                    +{a.profitPct}%
                  </div>
                  <div style={{ fontSize: 10, color: "var(--text3)" }}>guaranteed</div>
                </div>
              </div>
            ))}
          </div>

          {/* Bankroll snapshot */}
          <div className="card">
            <div className="section-header">
              <div className="section-title">💰 Bankroll</div>
              <button className="btn btn-ghost" style={{ fontSize: 11, padding: "5px 10px" }} onClick={() => onNavigate("bankroll")}>
                Manage →
              </button>
            </div>
            <div style={{ padding: 16 }}>
              {loading || !bankroll ? (
                <div style={{ color: "var(--text3)", fontSize: 13, textAlign: "center", padding: "12px 0" }}>
                  {loading ? "Loading..." : "Set your bankroll to get started"}
                </div>
              ) : (
                <>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 10, color: "var(--text3)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Current Balance</div>
                    <div style={{ fontFamily: "var(--font-display)", fontSize: 26, fontWeight: 800, color: "var(--accent)" }}>
                      P {bankroll.currentAmount?.toFixed(2)}
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    <MiniStat label="Health" value={`${bankroll.healthScore}/100`} color={bankroll.healthScore > 70 ? "var(--success)" : bankroll.healthScore > 40 ? "var(--warning)" : "var(--danger)"} />
                    <MiniStat label="Monthly P&L" value={`P${bankroll.monthlyPL?.toFixed(0)}`} color={bankroll.monthlyPL >= 0 ? "var(--success)" : "var(--danger)"} />
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 10, color: "var(--text3)", marginBottom: 4 }}>Monthly target progress</div>
                    <div className="progress">
                      <div className="progress-fill" style={{ width: `${Math.min(100, Math.max(0, (bankroll.monthlyPL / (bankroll.startingAmount * (bankroll.monthlyTargetPct / 100) || 1)) * 100)).toFixed(0)}%` }} />
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* CLV snapshot */}
      {clvStats && clvStats.history?.length > 0 && (
        <div className="card">
          <div className="section-header">
            <div>
              <div className="section-title">📈 Recent CLV Performance</div>
              <div className="section-sub">Positive = you beat the closing line</div>
            </div>
            <button className="btn btn-outline" style={{ fontSize: 11, padding: "5px 12px" }} onClick={() => onNavigate("clv")}>
              Full tracker →
            </button>
          </div>
          {isMobile ? (
            <div>
              {clvStats.history.slice(0, 5).map((b, i) => (
                <div key={i} style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.match}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>{b.bookmaker}</div>
                  </div>
                  <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 14, color: b.clvPct >= 0 ? 'var(--success)' : 'var(--danger)', flexShrink: 0, marginLeft: 12 }}>
                    {b.clvPct >= 0 ? '+' : ''}{b.clvPct}%
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Match</th>
                  <th>Book</th>
                  <th style={{ textAlign: "center" }}>Open odds</th>
                  <th style={{ textAlign: "center" }}>Close odds</th>
                  <th style={{ textAlign: "right" }}>CLV</th>
                </tr>
              </thead>
              <tbody>
                {clvStats.history.slice(0, 5).map((b, i) => (
                  <tr key={i}>
                    <td>{b.match}</td>
                    <td style={{ color: "var(--text3)", fontSize: 12 }}>{b.bookmaker}</td>
                    <td style={{ textAlign: "center", fontFamily: "var(--font-display)" }}>{b.openOdds}</td>
                    <td style={{ textAlign: "center", fontFamily: "var(--font-display)" }}>{b.closeOdds}</td>
                    <td style={{ textAlign: "right" }}>
                      <span style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 13, color: b.clvPct >= 0 ? "var(--success)" : "var(--danger)" }}>
                        {b.clvPct >= 0 ? "+" : ""}{b.clvPct}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}